/**
 * Recipient IANA timezone selector (moved from the old Automations plan editor, 12/10/2026)
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useMemo, useState, type FC } from 'react';

import { SearchableSelect } from '@/components/ui';
import { suggestTimezone } from '@/features/drop-generator/components/board-header/times/tz-suggestions';

export interface TimezoneSelectFieldProps {
  value: string | null;
  countryKey: string;
  onChange: (tz: string) => void;
}

function localTimeIn(tz: string, now: Date): string | null {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(now);
  } catch {
    return null;
  }
}

export const TimezoneSelectField: FC<TimezoneSelectFieldProps> = ({
  value,
  countryKey,
  onChange,
}) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const options = useMemo(
    () => Intl.supportedValuesOf('timeZone').map((tz) => ({ value: tz, label: tz })),
    [],
  );
  const suggestion = suggestTimezone(countryKey);
  const localTime = value ? localTimeIn(value, now) : null;
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-foreground">Country timezone</p>
      <SearchableSelect
        options={options}
        value={value ?? ''}
        onChange={(tz) => onChange(String(tz))}
        placeholder="Pick an IANA timezone…"
        searchPlaceholder="Search timezones…"
      />
      {suggestion && suggestion !== value && (
        <button
          type="button"
          className="text-xs text-primary underline-offset-2 hover:underline"
          onClick={() => onChange(suggestion)}
        >
          Suggested for {countryKey.toUpperCase()}: {suggestion}
        </button>
      )}
      {localTime && (
        <p className="text-xs text-muted-foreground">Current local time {localTime}</p>
      )}
    </div>
  );
};
